import { useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, AlertTriangle, CheckCircle2, ChevronRight, ClipboardList, FileImage,
  Play, RotateCcw, Sparkles, Trash2, Upload,
} from 'lucide-react';
import {
  ALL_CHECK_ITEMS, ASSESSMENT_QUESTIONS, CHECK_ITEM_GROUPS, CHECK_LEVEL_LABEL,
  FINDING_GROUP_LABEL, SECTION_LABEL, analyzeAssessment,
  type AssessmentAnswer, type AssessmentCheckState, type CheckLevel,
  type VisionAssessment,
} from '../../data/visionAssessment';
import { getGameById, type GameConfig } from '../../data/games';
import { useGameStore } from '../../store/gameStore';
import { playClick } from '../../utils/sounds';

type Step = 'intro' | 'questions' | 'checks' | 'analysis' | 'plan';

const STEP_LABEL: { id: Step; label: string }[] = [
  { id: 'questions', label: '问卷' },
  { id: 'checks', label: '检查结果' },
  { id: 'analysis', label: '异常分析' },
  { id: 'plan', label: '训练计划' },
];

const CHECK_LEVELS: CheckLevel[] = ['unknown', 'normal', 'mild', 'marked'];

interface Props {
  completedLevels: string[];
  onGameClick: (game: GameConfig) => void;
}

/** 把上传的报告压成小缩略图，只用于回看，不做文字识别 */
async function toThumbnail(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('读取图片失败'));
    reader.readAsDataURL(file);
  });
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('解析图片失败'));
    img.src = dataUrl;
  });
  const max = 220;
  const scale = Math.min(1, max / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.55);
}

export default function SmartRecommendPanel({ completedLevels, onGameClick }: Props) {
  const { visionAssessments, activeTrainingPlan, saveVisionAssessment, setActiveTrainingPlan, clearVisionAssessments } = useGameStore();
  const [step, setStep] = useState<Step>('intro');
  const [answers, setAnswers] = useState<AssessmentAnswer>({});
  const [checks, setChecks] = useState<AssessmentCheckState>({});
  const [reportImage, setReportImage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const latest = visionAssessments[visionAssessments.length - 1];

  const analysis = useMemo(() => analyzeAssessment(answers, checks), [answers, checks]);
  const answeredCount = ASSESSMENT_QUESTIONS.filter(q => typeof answers[q.id] === 'number').length;
  const markedChecks = ALL_CHECK_ITEMS.filter(i => checks[i.id] === 'mild' || checks[i.id] === 'marked').length;

  const resetDraft = () => {
    setAnswers({});
    setChecks({});
    setReportImage('');
    setUploadError('');
  };

  const startAssessment = () => {
    playClick();
    resetDraft();
    setStep('questions');
  };

  /** 回看已保存的评估：把上次的作答与检查结果回填，保证分析页一致 */
  const viewSavedAssessment = (target: Step) => {
    playClick();
    if (latest) {
      setAnswers(latest.answers);
      setChecks(latest.checks);
      setReportImage(latest.reportImage ?? '');
    }
    setStep(target);
  };

  const finishAssessment = () => {
    playClick();
    const plan = analysis.plans[0];
    const snapshot = plan
      ? {
        id: plan.id,
        name: plan.name,
        focus: plan.focus,
        duration: plan.duration,
        priority: plan.priority,
        games: plan.games,
        generatedAt: new Date().toISOString(),
      }
      : null;
    const record: VisionAssessment = {
      id: `assess-${Date.now()}`,
      answers,
      checks,
      reportImage: reportImage || undefined,
      findingIds: analysis.findings.map(f => f.id),
      createdAt: new Date().toISOString(),
    };
    // 回看已保存的评估时答案没动过，就不重复入库；但仍刷新当前方案，
    // 避免「分析页说 A 是主方案、计划页还显示旧的 B」这种前后不一致
    const unchanged = Boolean(latest)
      && JSON.stringify(latest.answers) === JSON.stringify(answers)
      && JSON.stringify(latest.checks) === JSON.stringify(checks);
    if (snapshot) {
      if (unchanged) setActiveTrainingPlan(snapshot);
      else saveVisionAssessment(record, snapshot);
    }
    setStep('plan');
  };

  const handleUpload = async (file?: File) => {
    if (!file) return;
    setUploadError('');
    try {
      setReportImage(await toThumbnail(file));
    } catch {
      setUploadError('图片处理失败，可跳过上传直接手动勾选检查项目');
    }
  };

  /* ---------------- 已评估：展示结果入口 ---------------- */

  if (step === 'intro' && latest && activeTrainingPlan) {
    const dateLabel = new Date(latest.createdAt).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
    return (
      <section className="cyber-panel cyber-wide cyber-assess">
        <div className="cyber-panel-head">
          <h2 className="cyber-panel-title">智能推荐 · 已生成方案</h2>
          <span className="cyber-panel-meta">{dateLabel}</span>
        </div>
        <div className="cyber-assess-summary">
          <div>
            <strong>{activeTrainingPlan.name}</strong>
            <p>聚焦 {activeTrainingPlan.focus} · 约 {activeTrainingPlan.duration} 分钟 · {activeTrainingPlan.games.length} 项</p>
          </div>
          <span className="cyber-assess-badge">{latest.findingIds.length > 0 ? `${latest.findingIds.length} 项异常` : '未见异常'}</span>
        </div>
        <div className="cyber-assess-games">
          {activeTrainingPlan.games.map(game => (
            <button key={game.id} className="cyber-plan-game" onClick={() => { playClick(); const target = getGameById(game.id); if (target) onGameClick(target); }}>
              <span className="cyber-plan-game-icon">{game.icon.slice(0, 2)}</span>
              <span className="cyber-plan-game-copy">
                <b>{game.name}</b>
                <em>{game.reason}</em>
              </span>
              <Play size={13} aria-hidden="true" />
            </button>
          ))}
        </div>
        <div className="cyber-assess-actions">
          <button className="cyber-btn primary" onClick={() => { playClick(); const first = getGameById(activeTrainingPlan.games[0]?.id ?? ''); if (first) onGameClick(first); }}>
            <Play size={13} /> 开始训练
          </button>
          <button className="cyber-btn ghost" onClick={() => viewSavedAssessment('analysis')}>
            <ClipboardList size={13} /> 查看分析
          </button>
          <button className="cyber-btn ghost" onClick={startAssessment}>
            <RotateCcw size={13} /> 重新评估
          </button>
          <button className="cyber-btn danger" onClick={() => { playClick(); clearVisionAssessments(); resetDraft(); setStep('intro'); }}>
            <Trash2 size={13} /> 清除记录
          </button>
        </div>
      </section>
    );
  }

  /* ---------------- 入口 ---------------- */

  if (step === 'intro') {
    return (
      <section className="cyber-panel cyber-wide cyber-assess">
        <div className="cyber-panel-head">
          <h2 className="cyber-panel-title">智能推荐</h2>
          <span className="cyber-panel-meta">问卷 → 检查 → 分析 → 计划</span>
        </div>
        <strong>先了解现状，再安排训练</strong>
        <p className="cyber-description">
          用一份问卷和一次检查结果录入，找出孩子当前双眼视功能的主要问题，自动生成匹配的训练计划。
        </p>
        <div className="cyber-steps">
          {STEP_LABEL.map((item, index) => (
            <div key={item.id} className="cyber-step">
              <b>{index + 1}</b>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        <button className="cyber-btn primary block" onClick={startAssessment}>
          <Sparkles size={14} /> 开始智能评估
        </button>
      </section>
    );
  }

  const activeIndex = STEP_LABEL.findIndex(s => s.id === step);
  const allAnswered = answeredCount === ASSESSMENT_QUESTIONS.length;

  return (
    <section className="cyber-panel cyber-wide cyber-assess">
      <div className="cyber-panel-head">
        <h2 className="cyber-panel-title">智能推荐 · {STEP_LABEL[activeIndex]?.label}</h2>
        <span className="cyber-panel-meta">{activeIndex + 1} / {STEP_LABEL.length}</span>
      </div>
      <div className="cyber-step-track">
        {STEP_LABEL.map((item, index) => (
          <i key={item.id} className={index < activeIndex ? 'done' : index === activeIndex ? 'now' : ''} />
        ))}
      </div>
      {latest && step !== 'plan' && <button className="cyber-btn ghost sm" onClick={() => setStep('intro')}>退出评估</button>}

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.22 }}>

          {/* 问卷 */}
          {step === 'questions' && <>
            <p className="cyber-description">按孩子最近 1-2 个月的真实表现作答，已完成 {answeredCount}/{ASSESSMENT_QUESTIONS.length} 题。</p>
            {(['function', 'symptom'] as const).map(section => (
              <div key={section} className="cyber-q-group">
                <h3 className="cyber-q-group-title">{SECTION_LABEL[section]}</h3>
                {ASSESSMENT_QUESTIONS.filter(q => q.section === section).map(question => (
                  <div key={question.id} className="cyber-q">
                    <p className="cyber-q-text">{question.text}</p>
                    {question.hint && <p className="cyber-q-hint">{question.hint}</p>}
                    <div className="cyber-options">
                      {question.options.map((option, index) => (
                        <button
                          key={option.label}
                          className={`cyber-option ${answers[question.id] === index ? 'on' : ''}`}
                          onClick={() => { playClick(); setAnswers(prev => ({ ...prev, [question.id]: index })); }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <div className="cyber-assess-actions">
              <button className="cyber-btn primary" disabled={!allAnswered} onClick={() => { playClick(); setStep('checks'); }}>
                {allAnswered ? <>下一步 · 录入检查结果 <ChevronRight size={13} /></> : `还需回答 ${ASSESSMENT_QUESTIONS.length - answeredCount} 题`}
              </button>
            </div>
          </>}

          {/* 检查结果录入 */}
          {step === 'checks' && <>
            <p className="cyber-description">
              有三种录入方式可任选：上传报告图片存档、手动勾选检查项目、或两者都做。图片只作留存，异常项请手动标注。
            </p>

            <div className="cyber-upload">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={event => { void handleUpload(event.target.files?.[0]); event.target.value = ''; }}
              />
              {reportImage ? (
                <div className="cyber-upload-preview">
                  <img src={reportImage} alt="检查报告缩略图" />
                  <div>
                    <b>报告已上传</b>
                    <em>仅作存档，请继续手动勾选异常项目</em>
                  </div>
                  <button className="cyber-btn ghost sm" onClick={() => { playClick(); setReportImage(''); }}>
                    <Trash2 size={12} /> 移除
                  </button>
                </div>
              ) : (
                <button className="cyber-btn ghost block" onClick={() => { playClick(); fileRef.current?.click(); }}>
                  <Upload size={13} /> 上传检查结果图片（可选）
                </button>
              )}
              {uploadError && <p className="cyber-error">{uploadError}</p>}
            </div>

            {CHECK_ITEM_GROUPS.map(group => (
              <div key={group.id} className="cyber-check-group">
                <h3 className="cyber-q-group-title">{group.name}</h3>
                {group.items.map(item => (
                  <div key={item.id} className="cyber-check-row">
                    <div className="cyber-check-name">
                      <b>{item.name}</b>
                      <em>正常范围 {item.normal}{item.unit ? ` ${item.unit}` : ''}</em>
                    </div>
                    <div className="cyber-seg">
                      {CHECK_LEVELS.map(level => (
                        <button
                          key={level}
                          className={`${(checks[item.id] ?? 'unknown') === level ? 'on' : ''} ${level}`}
                          onClick={() => { playClick(); setChecks(prev => ({ ...prev, [item.id]: level })); }}
                        >
                          {CHECK_LEVEL_LABEL[level]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div className="cyber-assess-actions">
              <button className="cyber-btn ghost" onClick={() => { playClick(); setStep('questions'); }}>上一步</button>
              <button className="cyber-btn primary" onClick={() => { playClick(); setStep('analysis'); }}>
                <Activity size={13} /> 查看异常分析
              </button>
            </div>
            <p className="cyber-note">已标注 {markedChecks} 项异常 · 未勾选的项目按「未检查」处理</p>
          </>}

          {/* 异常分析 */}
          {step === 'analysis' && <>
            <div className="cyber-analysis">
              <div className="cyber-analysis-score">
                <b>{analysis.healthScore}</b>
                <span>视觉功能指数</span>
              </div>
              <p>{analysis.summary}</p>
            </div>
            {analysis.findings.length === 0 ? (
              <div className="cyber-empty">
                <CheckCircle2 size={16} /> 未识别到明显异常，可按日常维护方案训练。
              </div>
            ) : (
              <div className="cyber-findings">
                {analysis.findings.map(finding => (
                  <div key={finding.id} className="cyber-finding">
                    <div className="cyber-finding-head">
                      <AlertTriangle size={13} />
                      <b>{finding.meta.label}</b>
                      <em>{FINDING_GROUP_LABEL[finding.meta.group]}</em>
                    </div>
                    <p>{finding.meta.detail}</p>
                    <div className="cyber-finding-src">
                      {finding.sources.map(source => <span key={source}>{source}</span>)}
                      <span className="cyber-finding-cat">建议方向：{finding.meta.categories.map(c => c === 'binocular' ? '双眼视' : c === 'myopia' ? '近视防控' : c === 'amblyopia' ? '斜弱视' : c === 'memory' ? '脑记忆' : c === 'fatigue' ? '视疲劳' : '空间知觉').join(' / ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="cyber-assess-actions">
              <button className="cyber-btn ghost" onClick={() => { playClick(); setStep('checks'); }}>上一步</button>
              <button className="cyber-btn primary" onClick={finishAssessment}>
                <Sparkles size={13} /> 生成训练计划
              </button>
            </div>
          </>}

          {/* 训练计划 */}
          {step === 'plan' && <>
            <div className="cyber-analysis">
              <div className="cyber-analysis-score">
                <b>{analysis.healthScore}</b>
                <span>视觉功能指数</span>
              </div>
              <p>{analysis.summary}</p>
            </div>
            {activeTrainingPlan && (
              <div className="cyber-plan primary-plan">
                <div className="cyber-plan-head">
                  <b>{activeTrainingPlan.name}</b>
                  <span>已设为当前方案 · 约 {activeTrainingPlan.duration} 分钟</span>
                </div>
                <div className="cyber-assess-games">
                  {activeTrainingPlan.games.map(game => (
                    <button key={game.id} className="cyber-plan-game" onClick={() => { playClick(); const target = getGameById(game.id); if (target) onGameClick(target); }}>
                      <span className="cyber-plan-game-icon">{game.icon.slice(0, 2)}</span>
                      <span className="cyber-plan-game-copy">
                        <b>{game.name}</b>
                        <em>{game.reason}</em>
                      </span>
                      <Play size={13} aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {analysis.plans.slice(1).map(plan => (
              <div key={plan.id} className="cyber-plan">
                <div className="cyber-plan-head">
                  <b>{plan.name}</b>
                  <span>{plan.focus} · {plan.duration} 分钟</span>
                </div>
                <p className="cyber-description">{plan.description}</p>
                <div className="cyber-assess-games">
                  {plan.games.map(game => (
                    <button key={game.id} className="cyber-plan-game" onClick={() => { playClick(); const target = getGameById(game.id); if (target) onGameClick(target); }}>
                      <span className="cyber-plan-game-icon">{game.icon.slice(0, 2)}</span>
                      <span className="cyber-plan-game-copy">
                        <b>{game.name}</b>
                        <em>{game.reason}</em>
                      </span>
                      <Play size={13} aria-hidden="true" />
                    </button>
                  ))}
                </div>
                <button className="cyber-btn ghost sm" onClick={() => { playClick(); setActiveTrainingPlan({ ...plan, generatedAt: new Date().toISOString() }); }}>
                  设为当前方案
                </button>
              </div>
            ))}
            {completedLevels.length === 0 && <p className="cyber-note">提示：先用「自由训练」熟悉一两款游戏，再按方案系统训练，效果更稳。</p>}
            <div className="cyber-assess-actions">
              <button className="cyber-btn primary" onClick={() => { playClick(); const first = getGameById(activeTrainingPlan?.games[0]?.id ?? ''); if (first) onGameClick(first); }}>
                <Play size={13} /> 开始训练
              </button>
              <button className="cyber-btn ghost" onClick={() => { playClick(); setStep('intro'); }}>
                <CheckCircle2 size={13} /> 完成
              </button>
            </div>
            <p className="cyber-note">
              <FileImage size={11} aria-hidden="true" /> 分析依据问卷与手动录入的检查结果，仅供训练安排参考，不能替代专业眼科诊断。
            </p>
          </>}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
